'use client';

import type { ComponentProps, SourceHTMLAttributes } from 'react';

import { Option } from 'effect';

import { useImageFallbackFlow } from '@vs-shop/headless/useImageFallbackFlow';

/**
 * Art-directed `<picture>` with a three-stage load chain:
 *
 *   primary → backend fallback → terminal fallback
 *
 * The browser tells us which slot it actually chose (`currentSrc`), so a failure
 * retries with *that* slot's own fallback rather than a blanket one, and the
 * retry renders as a single `<img>` — re-offering the alternatives would let the
 * browser pick the broken one again. A second failure paints
 * `terminalFallbackSrc`.
 *
 * This is the one `<picture>` implementation in the platform. It is the seam
 * counterpart of `Image`: that one is a single optimizable image an app may
 * alias to a framework component, whereas art direction across `<source>`
 * elements is rendered natively for every host — a framework image component
 * fights `<source>`, and the URLs arriving here are already sized.
 *
 * URLs are **final and renderable**. Every CDN/Liip/`{filter}` resolution and
 * every derivation of a backend retry URL stays app-side.
 */

/**
 * One slot's pair of URLs, as plain nullable strings.
 *
 * Plain rather than `Option` because these cross the Server → Client boundary:
 * `Option` values are Effect class instances, which RSC rejects. A server caller
 * holding `Option`s converts with `Option.getOrNull` at the boundary.
 */
export type PictureImage = {
  readonly primary: string | null;
  readonly backendFallback: string | null;
};

/**
 * `data-*` attributes, which React's HTML attribute types do not model on a
 * component's prop object. Declared so a caller can label the elements this
 * renders — a `data-slot` on the `<picture>`, the `<source>`s or the `<img>` —
 * from an object literal rather than only from a JSX attribute.
 */
type DataAttributes = {
  readonly [key: `data-${string}`]: string | undefined;
};

type PictureSourceProps = Omit<
  SourceHTMLAttributes<HTMLSourceElement>,
  'children' | 'src' | 'srcSet'
> &
  DataAttributes;

/**
 * One `<source>`: its selecting attributes — `media`, `type`, `sizes` — plus the
 * image to use when it matches. A slot whose `primary` did not resolve renders
 * no `<source>` at all, rather than an empty one that would win its media query
 * and paint nothing.
 */
export type PictureSource = PictureSourceProps & {
  readonly image: PictureImage;
};

export type PictureProps = Omit<ComponentProps<'picture'>, 'children'> &
  DataAttributes & {
    /** Absent or empty → a bare `<img>`, no art direction to offer. */
    readonly sources?: readonly PictureSource[];
    readonly image: PictureImage;
    readonly alt: string;
    readonly terminalFallbackSrc: string;
    /**
     * Attributes for the inner `<img>` — `className`, `loading`, `decoding`,
     * `fetchPriority`, `data-*`. Its `src`/`srcSet` are the flow's to set, and
     * its `ref` and `onError` drive the flow itself.
     */
    readonly imgProps?: Omit<ComponentProps<'img'>, 'src' | 'srcSet' | 'alt' | 'ref' | 'onError'> &
      DataAttributes;
  };

/** Internal, `Option`-shaped counterparts — the flow reasons in `Option`. */
type PictureImageOption = {
  readonly primary: Option.Option<string>;
  readonly backendFallback: Option.Option<string>;
};

type PictureSourceInternal = PictureSourceProps & {
  readonly image: PictureImageOption;
};

/** What the component renders at one stage of the flow. */
type PictureState = {
  readonly sources: readonly (PictureSourceProps & { readonly srcSet: Option.Option<string> })[];
  readonly image: Option.Option<string>;
};

const toOptionImage = (image: PictureImage): PictureImageOption => ({
  primary: Option.fromNullishOr(image.primary),
  backendFallback: Option.fromNullishOr(image.backendFallback),
});

const toOptionSource = ({ image, ...sourceProps }: PictureSource): PictureSourceInternal => ({
  ...sourceProps,
  image: toOptionImage(image),
});

function getPrimarySources(
  sources: readonly PictureSourceInternal[],
  image: PictureImageOption
): PictureState {
  return {
    sources: sources.map(({ image: sourceImage, ...sourceProps }) => ({
      ...sourceProps,
      srcSet: sourceImage.primary,
    })),
    image: image.primary,
  };
}

function getBackendFallbackSources(
  sources: readonly PictureSourceInternal[],
  image: PictureImageOption,
  currentSrc: string
): Option.Option<PictureState> {
  const selectedFallback = Option.firstSomeOf([
    ...sources.map((source) => getSelectedBackendFallback(source.image, currentSrc)),
    getSelectedBackendFallback(image, currentSrc),
  ]);

  return Option.map(selectedFallback, (fallback) => ({
    sources: [],
    image: Option.some(fallback),
  }));
}

function getSelectedBackendFallback(
  image: PictureImageOption,
  currentSrc: string
): Option.Option<string> {
  return Option.flatMap(image.primary, (primary) =>
    isSelectedImagePath(primary, currentSrc) ? image.backendFallback : Option.none<string>()
  );
}

/**
 * Whether `primary` is the URL the browser actually loaded. `currentSrc` is
 * absolute, so a relative URL is resolved against the document before comparing.
 */
function isSelectedImagePath(primary: string, currentSrc: string): boolean {
  if (primary === currentSrc) return true;

  return new URL(primary, document.baseURI).href === currentSrc;
}

function getSourcePropsResetKey({ image: _image, ...sourceProps }: PictureSourceInternal): string {
  return JSON.stringify(sourceProps);
}

function getResetKey(
  sources: readonly PictureSourceInternal[],
  image: PictureImageOption,
  terminalFallbackSrc: string
): string {
  return [
    ...sources.flatMap((source) => [
      getSourcePropsResetKey(source),
      Option.getOrUndefined(source.image.primary),
      Option.getOrUndefined(source.image.backendFallback),
    ]),
    Option.getOrUndefined(image.primary),
    Option.getOrUndefined(image.backendFallback),
    terminalFallbackSrc,
  ].join('|');
}

function getImgSrc(state: PictureState, terminalFallbackSrc: string): string {
  return Option.getOrElse(state.image, () => terminalFallbackSrc);
}

export default function Picture({
  sources = [],
  image,
  alt,
  terminalFallbackSrc,
  imgProps,
  ...pictureProps
}: PictureProps) {
  const internalSources = sources.map(toOptionSource);
  const internalImage = toOptionImage(image);

  const primary = getPrimarySources(internalSources, internalImage);

  const { stage, current, imgRef, isError, handleError } = useImageFallbackFlow<PictureState>({
    primary,
    fallback: Option.none<PictureState>(),
    resolveFallback: (img) =>
      getBackendFallbackSources(internalSources, internalImage, img.currentSrc || img.src),
    resetKey: getResetKey(internalSources, internalImage, terminalFallbackSrc),
  });

  const currentSources: PictureState = isError
    ? { sources: [], image: Option.some(terminalFallbackSrc) }
    : current;

  const imgSrc = getImgSrc(currentSources, terminalFallbackSrc);

  // Remount on every stage change: an `<img>` that has already failed keeps its
  // broken state when only its attributes change.
  return (
    <picture key={stage} {...pictureProps}>
      {currentSources.sources.map(({ srcSet, ...sourceProps }, index) =>
        Option.match(srcSet, {
          onNone: () => null,
          onSome: (value) => (
            <source key={`${String(index)}:${value}`} {...sourceProps} srcSet={value} />
          ),
        })
      )}
      <img
        {...imgProps}
        ref={imgRef}
        src={imgSrc}
        alt={alt}
        onError={(event) => {
          handleError(event.currentTarget);
        }}
      />
    </picture>
  );
}
